/**
 * hz-click 事件指令处理器
 */

import type {
  DataValue,
  DataObject,
  DataArray,
  EventHandler,
  DragState,
  GetDataFn,
} from "../types";

/**
 * hz-click 事件指令处理器
 */
export class ClickDirective {
  /** 事件处理器映射 */
  private eventHandlers: Map<Element, EventHandler> = new Map();

  /** 拖拽状态 */
  private dragState: DragState = {
    element: null,
    startX: 0,
    startY: 0,
    isDragging: false,
  };

  /**
   * 绑定 hz-click 事件
   * @param root 根元素
   * @param context 方法执行的上下文对象
   * @param getData 获取数据的函数
   */
  bind(root: Element, context: object, getData: GetDataFn): void {
    const elements = Array.from(root.querySelectorAll("[hz-click]"));
    if (root.hasAttribute("hz-click")) {
      elements.push(root);
    }

    elements.forEach((element: Element): void => {
      let onClickAttr = element.getAttribute("hz-click");
      if (onClickAttr == null) {
        return;
      }

      // 严格校验语法
      onClickAttr = onClickAttr.trim();
      if (onClickAttr.startsWith("{") && onClickAttr.endsWith("}")) {
        // 必须带 this.
        const inner = onClickAttr.slice(1, -1).trim();
        if (!inner.startsWith("this.")) {
          console.warn(
            `hz-click syntax error: "${onClickAttr}". When using curly braces {}, expression must start with "this.".`,
          );
          return;
        }
        onClickAttr = inner;
      } else {
        // 必须不带 this.
        if (onClickAttr.startsWith("this.")) {
          console.warn(
            `hz-click syntax error: "${onClickAttr}". "this." prefix is only allowed within curly braces {}.`,
          );
          return;
        }
      }

      // 如果已经绑定过，不要重复绑定
      if (this.eventHandlers.has(element)) {
        return;
      }

      // 创建新的事件处理器
      const handler: EventHandler = (event: Event): void => {
        if (this.dragState.isDragging && this.dragState.element === element) {
          this.dragState.isDragging = false;
          this.dragState.element = null;
          return;
        }
        this.executeHandler(onClickAttr, event, context, getData);
      };

      // 绑定鼠标事件来检测拖拽
      element.addEventListener(
        "mousedown",
        this.handleMouseDown.bind(this) as EventListener,
      );
      element.addEventListener(
        "mousemove",
        this.handleMouseMove.bind(this) as EventListener,
      );
      element.addEventListener("click", handler, false);
      this.eventHandlers.set(element, handler);
    });
  }

  /**
   * 处理鼠标按下事件
   */
  private handleMouseDown(event: MouseEvent): void {
    const element = event.currentTarget as Element;
    this.dragState = {
      element,
      startX: event.clientX,
      startY: event.clientY,
      isDragging: false,
    };
  }

  /**
   * 处理鼠标移动事件
   */
  private handleMouseMove(event: MouseEvent): void {
    if (!this.dragState.element) {
      return;
    }

    const moveX = Math.abs(event.clientX - this.dragState.startX);
    const moveY = Math.abs(event.clientY - this.dragState.startY);

    const dragThreshold = 5;
    if (moveX > dragThreshold || moveY > dragThreshold) {
      this.dragState.isDragging = true;
    }
  }

  /**
   * 执行点击处理器
   */
  private executeHandler(
    handlerString: string,
    event: Event,
    context: object,
    getData: GetDataFn,
  ): void {
    try {
      const match = handlerString.match(/^(?:this\.)?(\w+)\(([^)]*)\)$/);
      if (!match) {
        console.warn(`Invalid click handler format: ${handlerString}`);
        return;
      }

      const methodName = match[1];
      const argsString: string = match[2];

      const args: DataValue[] = this.parseArguments(argsString, getData);
      const processedArgs: DataValue[] = this.processEventArguments(
        args,
        event,
      );

      const fn = (context as Record<string, unknown>)[methodName];
      if (typeof fn === "function") {
        fn.apply(context, processedArgs);
      }
    } catch (error) {
      console.error("Error executing click handler:", error);
    }
  }

  /**
   * 处理事件参数，替换 $event 占位符
   */
  private processEventArguments(args: DataValue[], event: Event): DataValue[] {
    return args.map((arg: DataValue): DataValue => {
      return arg === "$event" ? event : arg;
    });
  }

  /**
   * 解析函数参数字符串
   */
  private parseArguments(argsString: string, getData: GetDataFn): DataValue[] {
    const trimmed = argsString.trim();
    if (!trimmed) {
      return [];
    }

    const args: DataValue[] = [];
    let current = "";
    let inString = false;
    let stringChar = "";
    let parentheses = 0;
    let brackets = 0;
    let braces = 0;

    for (let i = 0; i < trimmed.length; i++) {
      const char: string = trimmed[i];

      if (!inString) {
        if (char === '"' || char === "'") {
          inString = true;
          stringChar = char;
          current += char;
          continue;
        } else if (char === "(") {
          parentheses++;
        } else if (char === ")") {
          parentheses--;
        } else if (char === "[") {
          brackets++;
        } else if (char === "]") {
          brackets--;
        } else if (char === "{") {
          braces++;
        } else if (char === "}") {
          braces--;
        } else if (
          char === "," &&
          parentheses === 0 &&
          brackets === 0 &&
          braces === 0
        ) {
          args.push(this.parseValue(current.trim(), getData));
          current = "";
          continue;
        }
      } else {
        if (char === stringChar && trimmed[i - 1] !== "\\") {
          inString = false;
          stringChar = "";
        }
      }

      current += char;
    }

    if (current.trim()) {
      args.push(this.parseValue(current.trim(), getData));
    }

    return args;
  }

  /**
   * 解析单个参数值
   */
  private parseValue(value: string, getData: GetDataFn): DataValue {
    const trimmed = value.trim();

    // 变量引用 {this.xxx} (保留兼容性)
    const variableMatchWithBraces = trimmed.match(/^\{\s*this\.([^}]+)\s*}$/);
    if (variableMatchWithBraces) {
      const key = variableMatchWithBraces[1].trim();
      const dataValue = getData(key);
      return dataValue !== undefined ? dataValue : trimmed;
    }

    // 变量引用 (this.xxx, $scope.xxx, etc)
    const variableMatch = trimmed.match(/^([a-zA-Z_$][a-zA-Z0-9_$.]*)$/);
    if (variableMatch) {
      // 排除保留字和字面量
      if (
        ["true", "false", "null", "undefined", "$event"].includes(
          variableMatch[1],
        )
      ) {
        // let the literal checks below handle it
      } else {
        const key = variableMatch[1].trim();
        const dataValue = getData(key);
        return dataValue !== undefined ? dataValue : trimmed;
      }
    }

    // 字符串
    if (
      (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
      (trimmed.startsWith("'") && trimmed.endsWith("'"))
    ) {
      return trimmed.slice(1, -1).replace(/\\'/g, "'").replace(/\\"/g, '"');
    }

    // 数字
    if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
      return Number(trimmed);
    }

    // 布尔 / null / undefined
    if (trimmed === "true") {
      return true;
    }
    if (trimmed === "false") {
      return false;
    }
    if (trimmed === "null") {
      return null;
    }
    if (trimmed === "undefined") {
      return undefined;
    }

    // $event 占位
    if (trimmed === "$event") {
      return "$event";
    }

    // 对象或数组（尝试 JSON 解析）
    if (
      (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
      (trimmed.startsWith("[") && trimmed.endsWith("]"))
    ) {
      try {
        const parsed = JSON.parse(trimmed) as unknown;
        return this.validateParsedValue(parsed);
      } catch {
        return trimmed;
      }
    }

    return trimmed;
  }

  /**
   * 验证并转换解析后的值
   */
  private validateParsedValue(value: unknown): DataValue {
    if (
      value === null ||
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean" ||
      value === undefined
    ) {
      return value as DataValue;
    }

    if (Array.isArray(value)) {
      return value.map((item) => this.validateParsedValue(item)) as DataArray;
    }

    if (typeof value === "object") {
      const obj: DataObject = {};
      for (const [key, val] of Object.entries(value)) {
        obj[key] = this.validateParsedValue(val);
      }
      return obj;
    }

    return String(value);
  }

  /**
   * 解除所有事件绑定
   */
  unbindAll(): void {
    this.eventHandlers.forEach((handler: EventHandler, element: Element) => {
      element.removeEventListener("click", handler);
    });
    this.eventHandlers.clear();
  }
}
